/**
 * FeedbackFormModal - 버그&제안 피드백 제출 폼 모달
 *
 * - 제목: "버그&제안 피드백"
 * - 라디오: 버그 신고 / 개선 제안
 * - 버그일 때만 Tier 1/2 진단 데이터 수집
 * - 스크린샷 미리보기 + 추가 첨부
 * - Draft 이미지(URL) 첨부 지원 → 제출 시 소비(삭제)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Trans, t } from '@lingui/macro';
import { Loader2, ImagePlus, X, MessageSquareWarning, Pencil, Images } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';

import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';

import type { FeedbackType, Feedback, FeedbackEnvironment, FeedbackDiagnostics, DraftImage, ScreenshotEntry } from '../../models/Feedback';
import { createDefaultFeedback, emptyDiagnostics } from '../../models/Feedback';
import { collectAll } from './FeedbackEnvironment';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { Capacitor } from '@capacitor/core';
import { useToast } from '../../components/ui/use-toast';
import { ScreenshotMaskingEditor } from './ScreenshotMaskingEditor';
import { FeedbackDraftImagesModal } from './FeedbackDraftImagesModal';

// ── AI 자동 제목 생성 (설명 첫 줄 기반, 최대 80자) ──
function generateTitle(description: string): string {
  const firstLine = description.split('\n').find(l => l.trim())?.trim() || '';
  if (firstLine.length <= 80) return firstLine;
  return firstLine.substring(0, 77) + '...';
}

// ── AI 자동 태그 생성 (키워드 기반) ──
const TAG_KEYWORDS: Record<string, string[]> = {
  UI: ['화면', 'ui', 'css', '디자인', '레이아웃', '색상', '버튼', '폰트', 'icon', '아이콘', 'dark', '다크', 'style'],
  성능: ['느림', '느려', '렉', '성능', 'performance', 'slow', '로딩', 'loading', '버벅', 'lag', 'freeze'],
  크래시: ['crash', '크래시', '앱이 죽', '강제 종료', '꺼짐', '에러', 'error', '오류', '실패'],
  로그인: ['로그인', 'login', '인증', 'auth', '비밀번호', 'password', '구글', 'google', '애플', 'apple'],
  채팅: ['채팅', 'chat', '메시지', 'message', '알림', 'notification', '스레드', 'thread'],
  파일: ['파일', 'file', '업로드', 'upload', '다운로드', 'download', '이미지', 'image', '사진'],
  AI: ['ai', 'bot', '봇', '소리봇', 'soribot', '메모', 'memo'],
  모바일: ['모바일', 'mobile', 'ios', 'android', '앱', 'app', '터치'],
  알림: ['알림', 'notification', 'push', '푸시', '뱃지', 'badge'],
};

function generateTags(description: string, type: FeedbackType): string[] {
  const lower = description.toLowerCase();
  const tags: string[] = [];

  // 유형 태그
  tags.push(type === 'bug' ? '버그' : '제안');

  // 키워드 매칭
  for (const [tag, keywords] of Object.entries(TAG_KEYWORDS)) {
    if (keywords.some(kw => lower.includes(kw))) {
      tags.push(tag);
    }
  }

  return tags.slice(0, 5); // 최대 5개
}

interface FeedbackFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultType?: FeedbackType;
  /** 스크린샷 캡처로 전달받은 이미지 Blob */
  preAttachedScreenshots?: Blob[];
  /** Draft 이미지 객체 배열 */
  preAttachedDraftImages?: DraftImage[];
  /** 기존 드래프트 편집 시 */
  existingDraft?: Feedback | null;
}

export const FeedbackFormModal: React.FC<FeedbackFormModalProps> = ({
  open, onOpenChange, defaultType = 'bug',
  preAttachedScreenshots = [],
  preAttachedDraftImages = [],
  existingDraft = null,
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── 폼 상태 ──
  const [type, setType] = useState<FeedbackType>(defaultType);
  const [description, setDescription] = useState('');
  const [screenshotBlobs, setScreenshotBlobs] = useState<Blob[]>(preAttachedScreenshots);
  const [screenshotPreviews, setScreenshotPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Draft 이미지 URL (이미 업로드된 것들)
  const [draftUrls, setDraftUrls] = useState<string[]>(preAttachedDraftImages.map(d => d.imageUrl));
  // Draft 이미지 전체 객체 보관 (screenshotEntries 생성용)
  const [selectedDraftImages, setSelectedDraftImages] = useState<DraftImage[]>(preAttachedDraftImages);

  // Draft 이미지 피커 열기
  const [showDraftPicker, setShowDraftPicker] = useState(false);

  // ── Tier 1/2 수집 결과 (버그일 때만, 유저에게 표시하지 않음) ──
  const [environment, setEnvironment] = useState<FeedbackEnvironment | null>(null);
  const [diagnostics, setDiagnostics] = useState<FeedbackDiagnostics | null>(null);

  // ── 스크린샷 편집 (마스킹) 상태 ──
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // ── 기존 Draft의 이미 저장된 스크린샷 보존용 ──
  const [existingScreenshots, setExistingScreenshots] = useState<ScreenshotEntry[]>([]);

  const isBug = type === 'bug';

  // ── 모달 열릴 때 + 유형을 버그로 변경할 때 환경 수집 ──
  useEffect(() => {
    if (open) {
      if (existingDraft) {
        setType(existingDraft.type);
        setDescription(existingDraft.description || '');
        setEnvironment(existingDraft.environment || null);
        setDiagnostics(existingDraft.diagnostics || null);
        setExistingScreenshots(existingDraft.screenshots || []);
      } else if (isBug) {
        const data = collectAll();
        setEnvironment(data.environment);
        setDiagnostics(data.diagnostics);
      } else {
        setEnvironment(null);
        setDiagnostics(null);
      }
    }
  }, [open, isBug, existingDraft]);

  // ── preAttachedScreenshots 변경 시 미리보기 갱신 ──
  useEffect(() => {
    if (preAttachedScreenshots.length > 0) {
      setScreenshotBlobs(preAttachedScreenshots);
    }
  }, [preAttachedScreenshots]);

  // ── preAttachedDraftImages 변경 시 ──
  useEffect(() => {
    if (preAttachedDraftImages.length > 0) {
      setDraftUrls(preAttachedDraftImages.map(d => d.imageUrl));
      setSelectedDraftImages(preAttachedDraftImages);
    }
  }, [preAttachedDraftImages]);

  // ── Blob → 미리보기 URL 생성 ──
  useEffect(() => {
    const urls = screenshotBlobs.map(b => URL.createObjectURL(b));
    setScreenshotPreviews(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [screenshotBlobs]);

  // ── 파일 선택 핸들러 ──
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newBlobs = Array.from(files).filter(f => f.type.startsWith('image/'));
    setScreenshotBlobs(prev => [...prev, ...newBlobs]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // ── 새 스크린샷(Blob) 제거 ──
  const removeScreenshot = useCallback((index: number) => {
    setScreenshotBlobs(prev => prev.filter((_, i) => i !== index));
  }, []);

  // 2. Draft 이미지 제거
  const handleRemoveDraftUrl = (index: number) => {
    setDraftUrls(prev => prev.filter((_, i) => i !== index));
    setSelectedDraftImages(prev => prev.filter((_, i) => i !== index));
  };

  // ── 스크린샷 편집 완료 (마스킹 적용) ──
  const handleEditConfirm = useCallback((maskedBlob: Blob) => {
    if (editingIndex !== null) {
      setScreenshotBlobs(prev => prev.map((b, i) => i === editingIndex ? maskedBlob : b));
    }
    setEditingIndex(null);
  }, [editingIndex]);

  // ── Draft 이미지 피커에서 선택 ──
  const handleDraftImagesSelected = useCallback((selectedImages: DraftImage[]) => {
    setDraftUrls(prev => [...prev, ...selectedImages.map(d => d.imageUrl)]);
    setSelectedDraftImages(prev => [...prev, ...selectedImages]);
    setShowDraftPicker(false);
  }, []);

  // ── 제출 ──
  const handleSubmit = async () => {
    if (!description.trim()) {
      toast({ variant: 'destructive', title: t`Please describe the issue or suggestion` });
      return;
    }

    setSubmitting(true);
    try {
      const descText = description.trim();
      const autoTitle = generateTitle(descText);
      const autoTags = generateTags(descText, type);

      const feedback: Feedback = existingDraft ? { ...existingDraft } : createDefaultFeedback(
        type,
        My.user.id,
        My.user.name,
        My.user.email,
        My.user.photoUrl,
      ) as Feedback;

      feedback.type = type;
      feedback.title = autoTitle || 'Untitled';
      feedback.description = descText;
      feedback.tags = autoTags;
      feedback.status = 'submitted';
      feedback.updatedAt = new Date();

      if (isBug && environment) feedback.environment = environment;
      if (isBug && diagnostics) feedback.diagnostics = diagnostics;

      if (!feedback.environment) {
        feedback.environment = (environment || collectAll().environment) as FeedbackEnvironment;
      }
      if (!feedback.diagnostics) {
        feedback.diagnostics = (diagnostics || emptyDiagnostics());
      }

      // 스크린샷 수집
      const finalScreenshots: ScreenshotEntry[] = [];

      // 기존 스크린샷 보존 (수정 모드일 때 이미 올라가 있는 파일들)
      existingScreenshots.forEach(s => {
        finalScreenshots.push(s);
      });

      // Draft 선택된 캡처본들은 메타데이터 그대로 보존
      selectedDraftImages.forEach(d => {
        finalScreenshots.push({
          imageUrl: d.imageUrl,
          capturedAt: d.capturedAt,
          platform: d.platform,
          currentUrl: d.currentUrl,
          environment: d.environment,
          diagnostics: d.diagnostics,
        });
      });

      // 새로 폼에서 파일업로드한 캡처(새 Blob)
      if (screenshotBlobs.length > 0) {
        const uploadPromises = screenshotBlobs.map(blob =>
          Fire.feedback.uploadScreenshot(blob, feedback.id)
        );
        const newUrls = await Promise.all(uploadPromises);
        // 이 경우엔 방금 수집한 현재 환경데이터 연동
        newUrls.forEach(url => {
          finalScreenshots.push({
            imageUrl: url,
            capturedAt: new Date(),
            platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web',
            currentUrl: window.location.href,
            environment: feedback.environment,
            diagnostics: feedback.diagnostics,
          });
        });
      }

      feedback.screenshots = finalScreenshots;

      // Firestore에 저장
      await Fire.feedback.submitFeedback(feedback);

      // 첨부된 Draft 이미지 소비(삭제)
      if (selectedDraftImages.length > 0) {
        await Fire.feedback.deleteDraftImages(My.user.id, selectedDraftImages.map(d => d.id));
      }

      toast({ title: t`Feedback submitted successfully` });
      resetForm();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      toast({ variant: 'destructive', title: t`Failed to submit feedback` });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    setSubmitting(true);
    try {
      const descText = description.trim();
      const autoTitle = descText ? generateTitle(descText) : t`Untitled Draft`;
      const autoTags = descText ? generateTags(descText, type) : [type === 'bug' ? '버그' : '제안'];

      const feedback: Feedback = existingDraft ? { ...existingDraft } : createDefaultFeedback(
        type,
        My.user.id,
        My.user.name,
        My.user.email,
        My.user.photoUrl,
      ) as Feedback;

      feedback.type = type;
      feedback.title = autoTitle;
      feedback.description = descText;
      feedback.tags = autoTags;
      feedback.status = 'draft';
      feedback.updatedAt = new Date();

      if (isBug && environment) feedback.environment = environment;
      if (isBug && diagnostics) feedback.diagnostics = diagnostics;

      if (!feedback.environment) {
        feedback.environment = (environment || collectAll().environment) as FeedbackEnvironment;
      }
      if (!feedback.diagnostics) {
        feedback.diagnostics = (diagnostics || emptyDiagnostics());
      }

      // 스크린샷 수집
      const finalScreenshots: ScreenshotEntry[] = [];

      existingScreenshots.forEach(s => {
        finalScreenshots.push(s);
      });

      selectedDraftImages.forEach(d => {
        finalScreenshots.push({
          imageUrl: d.imageUrl,
          capturedAt: d.capturedAt,
          platform: d.platform,
          currentUrl: d.currentUrl,
          environment: d.environment,
          diagnostics: d.diagnostics,
        });
      });

      if (screenshotBlobs.length > 0) {
        const uploadPromises = screenshotBlobs.map(blob =>
          Fire.feedback.uploadScreenshot(blob, feedback.id)
        );
        const newUrls = await Promise.all(uploadPromises);
        newUrls.forEach(url => {
          finalScreenshots.push({
            imageUrl: url,
            capturedAt: new Date(),
            platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web',
            currentUrl: window.location.href,
            environment: feedback.environment,
            diagnostics: feedback.diagnostics,
          });
        });
      }

      feedback.screenshots = finalScreenshots;

      await Fire.feedback.submitFeedback(feedback);

      // 첨부된 Draft 이미지 소비
      if (selectedDraftImages.length > 0) {
        await Fire.feedback.deleteDraftImages(My.user.id, selectedDraftImages.map(d => d.id));
      }

      toast({ title: t`Draft saved successfully` });
      resetForm();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to save draft:', err);
      toast({ variant: 'destructive', title: t`Failed to save draft` });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setDescription('');
    setScreenshotBlobs([]);
    setDraftUrls([]);
    setSelectedDraftImages([]);
    setExistingScreenshots([]);
  };

  // 전체 이미지 개수 (Draft URL + Blob + 기존 Draft 스크린샷)
  const totalImages = draftUrls.length + screenshotPreviews.length + existingScreenshots.length;

  return (
    <>
    <Dialog
      open={open}
      modal={editingIndex === null && !showDraftPicker}
      onOpenChange={(v) => {
        if (editingIndex !== null || showDraftPicker) return;
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="max-w-[540px] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => {
          if (editingIndex !== null || showDraftPicker) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (editingIndex !== null || showDraftPicker) e.preventDefault();
        }}
      >        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareWarning className="w-5 h-5 text-primary" />
            <Trans>Bug & Suggestion Feedback</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Describe the issue or suggestion you'd like to share.</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ── 유형 선택 (라디오) ── */}
          <div className="space-y-2">
            <Label><Trans>Type</Trans></Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="feedback-type"
                  checked={isBug}
                  onChange={() => setType('bug')}
                  className="w-4 h-4 accent-red-500"
                />
                <span className={`text-sm font-medium ${isBug ? 'text-foreground' : 'text-muted-foreground'}`}>
                  🐛 <Trans>Bug Report</Trans>
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="feedback-type"
                  checked={!isBug}
                  onChange={() => setType('feature')}
                  className="w-4 h-4 accent-yellow-500"
                />
                <span className={`text-sm font-medium ${!isBug ? 'text-foreground' : 'text-muted-foreground'}`}>
                  💡 <Trans>Feature Request</Trans>
                </span>
              </label>
            </div>
          </div>

          {/* 설명 */}
          <div>
            <Label htmlFor="feedback-desc"><Trans>Description</Trans> *</Label>
            <Textarea
              id="feedback-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={isBug
                ? t`Describe the bug - what happened, what you expected, steps to reproduce...`
                : t`Describe your idea in detail...`
              }
              rows={6}
              autoFocus={!Capacitor.isNativePlatform()}
            />
          </div>

          {/* 스크린샷 */}
          <div>
            <Label><Trans>Screenshots</Trans></Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {/* Draft 이미지 URL (이미 업로드된 것들) */}
              {draftUrls.map((url, i) => (
                <div key={`draft-${i}`} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-primary/30 bg-primary/5">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  {/* 삭제 버튼 */}
                  <button
                    onClick={() => handleRemoveDraftUrl(i)}
                    className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                  {/* Draft 라벨 */}
                  <span className="absolute bottom-0.5 left-0.5 bg-primary/80 text-white text-meta-sm px-1 rounded">
                    Draft
                  </span>
                </div>
              ))}
              {/* 기존 스크린샷 (existingDraft) */}
              {existingScreenshots.map((scr, i) => (
                <div key={`existing-${i}`} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-border">
                  <img src={scr.imageUrl} alt="" className="w-full h-full object-cover" />
                  {/* 삭제 버튼 */}
                  <button
                    onClick={() => setExistingScreenshots(prev => prev.filter((_, idx) => idx !== i))}
                    className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
              {/* 새로 추가된 스크린샷 (Blob) */}
              {screenshotPreviews.map((url, i) => (
                <div key={i} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-border">
                  <img
                    src={url}
                    alt=""
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => setEditingIndex(i)}
                    title={t`Click to edit`}
                  />
                  {/* 편집 버튼 */}
                  <button
                    onClick={() => setEditingIndex(i)}
                    className="absolute bottom-0.5 left-0.5 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Pencil className="w-3 h-3 text-white" />
                  </button>
                  {/* 삭제 버튼 */}
                  <button
                    onClick={() => removeScreenshot(i)}
                    className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
              {/* 파일 추가 버튼 */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-1 hover:border-primary/50 transition-colors"
              >
                <ImagePlus className="w-5 h-5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground"><Trans>Add</Trans></span>
              </button>
              {/* Draft 이미지 첨부 버튼 */}
              <button
                onClick={() => setShowDraftPicker(true)}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-primary/30 flex flex-col items-center justify-center gap-1 hover:border-primary/50 transition-colors bg-primary/5"
              >
                <Images className="w-5 h-5 text-primary/70" />
                <span className="text-xs text-primary/70"><Trans>Drafts</Trans></span>
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>


        </div>

        <DialogFooter className="mt-4 flex flex-row justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} className="mr-auto">
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="secondary" onClick={handleSaveDraft} disabled={submitting}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Trans>Save Draft</Trans>
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !description.trim()}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Trans>Submit</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

      {/* Draft 이미지 피커 모달 */}
      {showDraftPicker && (
        <FeedbackDraftImagesModal
          open={true}
          onOpenChange={(v) => { if (!v) setShowDraftPicker(false); }}
          onCreateFeedback={handleDraftImagesSelected}
        />
      )}

      {/* 스크린샷 편집 (마스킹 편집기) — Dialog 위에 표시하기 위해 Portal 사용 */}
      {editingIndex !== null && screenshotBlobs[editingIndex] && createPortal(
        <ScreenshotMaskingEditor
          imageBlob={screenshotBlobs[editingIndex]}
          onConfirm={handleEditConfirm}
          onCancel={() => setEditingIndex(null)}
        />,
        document.body
      )}
    </>
  );
};
